<template>
  <div v-if="!loading">
    <!-- Above the worksheet, not next to the save button: whether this station
         is printing has to be visible on arrival, before anyone starts weighing
         — and the switch has to be reachable without scrolling back down. -->
    <!-- Rendered whatever the worksheet's status: printing on save only happens
         while entries are being added, but a sticker gets torn long afterwards
         and has to be replaceable from the entry list. The component shows
         nothing on a phone without a printer. -->
    <auto-label-print
      ref="autoLabelPrint"
      :entry-mode="stage === 1 && localWorksheet.status === 'PENDING'"
      :worksheet="localWorksheet"
      :product-info="productInfo"
    />

    <worksheet-header
      :worksheet.sync="localWorksheet"
      :worksheetTypeFields="worksheetTypeFields"
      :sum.sync="sum"
      :productInfo="productInfo"
      @jobUpdated="jobChanged"
    />

    <hr />

    <div
      class="text-center"
      v-if="localWorksheet.status === 'SUBMITTED' || localWorksheet.status === 'USED'"
    >
      <v-alert outlined color="success" class="mb-7 text-center">
        <span v-if="localWorksheet.status === 'SUBMITTED'" class="my-auto">
          {{ $vuetify.lang.t("$vuetify.worksheet.submittedOn") }}
          {{ localWorksheet.submittedTime | formatDateTime }}
        </span>
        <span v-else>{{ $vuetify.lang.t("$vuetify.worksheet.worksheetUsed") }}</span>
        <v-btn
          @click="toggleStatus"
          class="ma-4"
          outlined
          v-if="isManagement && localWorksheet.status === 'SUBMITTED'"
        >
          Change Status
        </v-btn>
      </v-alert>
    </div>

    <v-fade-transition hide-on-leave="true">
      <worksheet-settings
        v-if="stage === 0 && hasStage0"
        @onComplete="completePrerequisite"
        @update:worksheet="handleWorksheetUpdate"
        :worksheet.sync="localWorksheet"
        :worksheetTypeFields="worksheetTypeFields"
        :productInfo="productInfo"
      />
    </v-fade-transition>

    <v-fade-transition hide-on-leave="true">
      <worksheet-entry
        v-if="stage === 1 && localWorksheet.status === 'PENDING'"
        :worksheet="localWorksheet"
        :worksheetTypeFields="worksheetTypeFields"
        :sum="sum"
        :currentEntry="currentEntry"
        :productInfo="productInfo"
        :key="currentEntry.id || 'new-entry'"
        @add-entry="handleAddEntry"
        @update-entry="handleUpdateEntry"
        @update-entries="handleUpdateEntries"
        @update:currentEntry="handleUpdateCurrentEntry"
      />
    </v-fade-transition>

    <v-divider class="mt-4" />

    <div class="d-flex justify-space-between">
      <div v-if="localWorksheet.memo">
        <v-subheader>
          {{ $vuetify.lang.t("$vuetify.base.memo") }}:
          <div class="ml-2">
            {{ localWorksheet.memo }}
          </div>
        </v-subheader>
      </div>
    </div>

    <v-bottom-navigation grow app>
      <v-btn
        v-if="
          (stage === 1 && hasStage0) ||
          (localWorksheet.status === 'SUBMITTED' &&
            canEditSubmittedProcessTime)
        "
        @click="stage = 0"
      >
        <span>{{ $vuetify.lang.t("$vuetify.base.settings") }}</span>
        <v-icon>mdi-cog-outline</v-icon>
      </v-btn>
      <v-btn v-if="!hasEntries" @click="deleteWorksheet">
        <span>{{ $vuetify.lang.t("$vuetify.base.delete") }}</span>
        <v-icon color="red">mdi-trash-can</v-icon>
      </v-btn>
      <v-btn @click="entriesDialog = true" v-if="hasEntries">
        <span>
          {{ groupedEntryCount }}
          {{ $vuetify.lang.t("$vuetify.base.entries") }}
        </span>
        <v-icon>mdi-format-list-numbered</v-icon>
      </v-btn>
      <v-btn
        @click="
          timestamp = Date.now();
          notesDialog = true;
        "
        v-if="worksheetTypeFields.hasNotes"
      >
        <span>{{ $vuetify.lang.t("$vuetify.base.notes") }}</span>
        <v-icon>mdi-note-edit-outline</v-icon>
      </v-btn>
      <v-btn v-if="stage === 0 && !submitted" @click="completePrerequisite">
        <span>{{ $vuetify.lang.t("$vuetify.base.entries") }}</span>
        <v-icon>mdi-pencil-outline</v-icon>
      </v-btn>
      <v-btn
        :disabled="loading"
        v-if="hasEntries && !submitted"
        @click="submitWorksheet"
      >
        <span>{{ $vuetify.lang.t("$vuetify.base.submit") }}</span>
        <v-icon>mdi-arrow-up-circle-outline</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <v-dialog
      style="z-index: 1000"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="entriesDialog"
    >
      <v-card>
        <v-card-title class="d-flex justify-end">
          <v-btn icon @click="entriesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <worksheet-items
            :worksheet="localWorksheet"
            :sum="sum"
            :worksheetTypeFields="worksheetTypeFields"
            :productInfo="productInfo"
            :editable="true"
            @editEntry="editEntry"
            @deletedEntry="handleDeleteEntry"
            @reprintEntry="reprintEntry"
            @refresh-worksheet="refreshWorksheet"
            @resolvedEntry="handleResolvedEntry"
          />
          <div class="d-flex justify-center mt-4" v-if="hasEntries && !isUsed && !isCancelled && hasInventoryEntries && worksheetTypeFields.hasInventorySelection && isFifoOrLifo">
            <v-btn color="warning" outlined @click="reassignAll">
              <v-icon left>mdi-swap-horizontal</v-icon>
              {{ $vuetify.lang.t("$vuetify.worksheet.reassignAll") }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      style="z-index: 1000"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="preSubmitDialog"
    >
      <v-card>
        <v-card-title class="d-flex justify-end">
          <v-btn icon @click="preSubmitDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <worksheet-entry-pre-submit
            :worksheet.sync="localWorksheet"
            :sum="sum"
            :weightUnit="weightUnit"
          />
        </v-card-text>
        <v-card-actions class="d-flex justify-center">
          <v-btn class="ma-8" @click="preSubmitDialog = false">
            {{ $vuetify.lang.t("$vuetify.base.cancel") }}
          </v-btn>
          <v-btn class="ma-8" color="primary" @click="submitWorksheet">
            {{ $vuetify.lang.t("$vuetify.base.submit") }}
            {{ $vuetify.lang.t("$vuetify.worksheet.worksheet") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <worksheet-notes
      v-model="notesDialog"
      :worksheet="localWorksheet"
      :key="timestamp"
    />
    <confirmation-dialog ref="confirm" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getProcessWorksheet, reassignAllInventory } from "@/services/processWorksheet";
import { sumWorksheetWeight } from "@/services/worksheetServices";

const WorksheetSettings = () =>
  import("@/components/processWorksheet/WorksheetSettings.vue");
const WorksheetEntryPreSubmit = () =>
  import("@/components/processWorksheet/WorksheetEntryPreSubmit.vue");
const WorksheetNotes = () =>
  import("@/components/processWorksheet/WorksheetNotes.vue");
const WorksheetHeader = () =>
  import("@/components/processWorksheet/WorksheetHeader.vue");
const WorksheetEntry = () =>
  import("@/components/processWorksheet/WorksheetEntry.vue");
const WorksheetItems = () =>
  import("@/components/processWorksheet/WorksheetItems.vue");

export default {
  props: {
    worksheet: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      refreshingWorksheet: false,
      validGrossWeight: false,
      showEntryNotes: false,
      addTime: Date.now(),
      lastWorksheetRefreshAt: 0,
      currentEntry: null,
      entriesDialog: false,
      preSubmitDialog: false,
      notesDialog: false,
      sum: { grossWeight: 0, netWeight: 0 },
      imageData: null,
      productInfo: null,
      timestamp: Date.now(),
      stage: 0, // 0 - supplier/weight-settings, 1 - enter weight;  2 - pre-submission
      localWorksheet: { ...this.worksheet }, // Local copy of worksheet
    };
  },
  computed: {
    ...mapGetters("auth", ["currentUser", "userRoles"]),
    ...mapGetters("worksheetTypeDefinition", ["worksheetTypeDefinitions"]),
    isManagement() {
      const permissibleRoles = ["Administrator", "Manager", "Factory"];
      if (
        this.userRoles &&
        this.userRoles.some((r) => permissibleRoles.includes(r))
      ) {
        return true;
      } else {
        return false;
      }
    },
    canEditSubmittedProcessTime() {
      const permissibleRoles = [
        "Administrator",
        "Manager",
        "Factory",
        "Technician",
      ];
      return (
        this.userRoles &&
        this.userRoles.some((role) => permissibleRoles.includes(role))
      );
    },
    weightUnit() {
      if (this.productInfo) {
        return this.productInfo.measureType === "Unit"
          ? this.productInfo.measureUnit
          : this.productInfo.measureWeight;
      } else {
        return null;
      }
    },
    entryIconsLayout() {
      let hasFiles = false;
      if (this.currentEntry && this.currentEntry.files) {
        hasFiles = this.currentEntry.files.length > 0;
      }

      let hasNotes = false;
      if (this.showEntryNotes || (this.currentEntry && this.currentEntry.notes)) {
        hasNotes = true;
      }

      const useColumn = hasFiles || hasNotes;
      return useColumn ? "flex-column" : "flex-row";
    },
    netWeight() {
      let grossWeight = 0;
      if (this.currentEntry && this.currentEntry.grossWeight) {
        grossWeight = this.currentEntry.grossWeight;
      }
      return grossWeight - (this.localWorksheet.deductibleWeight || 0);
    },
    hasEntries() {
      return (
        this.localWorksheet.entries &&
        Array.isArray(this.localWorksheet.entries) &&
        this.localWorksheet.entries.length > 0
      );
    },
    groupedEntryCount() {
      if (!this.hasEntries) return 0;

      // Count unique entry groups
      const groupIds = new Set();
      this.localWorksheet.entries.forEach(entry => {
        const groupId = entry.entryGroupId || entry.id;
        groupIds.add(groupId);
      });
      return groupIds.size;
    },
    hasInventoryEntries() {
      return (
        this.hasEntries &&
        this.localWorksheet.entries.some((e) => e.inventoryId)
      );
    },
    isFifoOrLifo() {
      const method = this.localWorksheet && this.localWorksheet.processInputMethod;
      return method === "FIFO" || method === "LIFO";
    },
    hasStage0() {
      if (this.localWorksheet.status === "SUBMITTED") {
        return true;
      }

      if (this.worksheetTypeFields) {
        return (
          this.worksheetTypeFields.hasWeightSettings ||
          this.worksheetTypeFields.hasSupplier ||
          this.worksheetTypeFields.hasCustomer ||
          this.worksheetTypeFields.hasDocumentNumber
        );
      } else {
        return false;
      }
    },
    hasPreSubmit() {
      const hasNetAdjWeight =
        this.worksheetTypeFields && this.worksheetTypeFields.hasNetAdjustmentWeight;
      return hasNetAdjWeight;
    },
    worksheetTypeFields() {
      if (
        this.worksheetTypeDefinitions &&
        this.worksheetTypeDefinitions.length > 0 &&
        this.localWorksheet.worksheetTypeId
      ) {
        return this.worksheetTypeDefinitions.find(
          (t) => t.id === this.localWorksheet.worksheetTypeId
        );
      } else {
        return {};
      }
    },

    submitted() {
      if (
        this.localWorksheet &&
        ["SUBMITTED", "USED"].includes(this.localWorksheet.status)
      ) {
        return true;
      } else {
        return false;
      }
    },

    isUsed() {
      return this.localWorksheet && this.localWorksheet.status === "USED";
    },

    isCancelled() {
      return this.localWorksheet && this.localWorksheet.status === "CANCELLED";
    },

    isMobile() {
      return this.$vuetify.breakpoint.name === "xs";
    },
  },
  components: {
    AutoLabelPrint: () =>
      import("@/components/processWorksheet/AutoLabelPrint.vue"),
    WorksheetItems,
    WorksheetNotes,
    WorksheetHeader,
    WorksheetEntry,
    WorksheetEntryPreSubmit,
    WorksheetSettings,
  },
  watch: {
    worksheet: {
      handler(newVal) {
        this.localWorksheet = { ...newVal };
      },
      deep: true,
      immediate: true,
    },
    entriesDialog(isOpen) {
      if (isOpen) {
        this.triggerWorksheetRefresh();
      }
    },
  },
  async created() {
    try {
      await this.fetchWorksheetTypeDefinitions();
      await this.refreshWorksheet({ initialize: true, silent: false });
    } catch (error) {
      this.addErrorMessage("Error fetching worksheet data.");
      console.error(error);
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    window.addEventListener("focus", this.handleWindowFocus);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  beforeDestroy() {
    window.removeEventListener("focus", this.handleWindowFocus);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  },
  methods: {
    ...mapActions("processWorksheet", [
      "submitProcessWorksheet",
      "deleteProcessWorksheet",
      "updateProcessWorksheet",
    ]),
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
      "addWarningMessage",
    ]),
    ...mapActions("worksheetTypeDefinition", ["fetchWorksheetTypeDefinitions"]),
    ...mapActions("categoryStore", ["fetchCategory"]),

    completePrerequisite() {
      this.sumWeight();
      this.currentEntry = {};
      this.stage = 1;
    },
    handleWindowFocus() {
      this.triggerWorksheetRefresh();
    },
    handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.triggerWorksheetRefresh();
      }
    },
    triggerWorksheetRefresh() {
      if (this.loading || !this.localWorksheet.id || this.refreshingWorksheet) {
        return;
      }

      const now = Date.now();
      if (now - this.lastWorksheetRefreshAt < 1000) {
        return;
      }

      this.refreshWorksheet({ silent: true });
    },
    async refreshWorksheet({ initialize = false, silent = true } = {}) {
      if (!this.localWorksheet.id) {
        return null;
      }

      this.refreshingWorksheet = true;

      try {
        const previousProductId = this.localWorksheet.productId;
        const response = await getProcessWorksheet(this.localWorksheet.id);
        const refreshedWorksheet = response.data || {};

        if (!Array.isArray(refreshedWorksheet.entries)) {
          refreshedWorksheet.entries = [];
        }

        this.localWorksheet = refreshedWorksheet;
        this.$emit("update:worksheet", this.localWorksheet);

        if (
          initialize ||
          !this.productInfo ||
          previousProductId !== refreshedWorksheet.productId
        ) {
          await this.loadProductInfo();
        }

        if (initialize) {
          if (this.hasEntries || !this.hasStage0) {
            this.currentEntry = {};
            this.stage = 1;
          }

          if (
            this.localWorksheet &&
            this.localWorksheet.status === "SUBMITTED" &&
            this.worksheetTypeFields.hasNotes
          ) {
            this.stage = 2;
          }
        }

        await this.sumWeight();
        return refreshedWorksheet;
      } catch (error) {
        if (!silent) {
          throw error;
        }
        console.error(error);
        return null;
      } finally {
        this.refreshingWorksheet = false;
        this.lastWorksheetRefreshAt = Date.now();
      }
    },
    async submitWorksheet() {
      let confirm = false;
      // Check if worksheet has pre-submit module
      if (this.hasPreSubmit && !this.preSubmitDialog) {
        this.preSubmitDialog = true;
      } else {
        confirm = await this.$refs.confirm.open(
          this.$vuetify.lang.t("$vuetify.base.confirmSubmission"),
          this.$vuetify.lang.t("$vuetify.worksheet.submitConfirmMsg")
        );
      }

      if (confirm) {
        this.submitProcessWorksheet(this.localWorksheet)
          .then((submittedWorksheet) => {
            this.addSuccessMessage(
              this.$vuetify.lang.t("$vuetify.worksheet.submitSuccessMsg")
            );

            this.localWorksheet = { ...submittedWorksheet };
            this.$emit("update:worksheet", this.localWorksheet);
            this.$emit("submitted", this.localWorksheet);
            if (this.preSubmitDialog) {
              this.preSubmitDialog = false;
            }
          })
          .catch((error) => {
            const message = error.data.message || "Error submitting worksheet";
            this.addErrorMessage(message);
          });
      }
    },
    async sumWeight() {
      this.sum = await sumWorksheetWeight(this.localWorksheet);
    },
    async loadProductInfo() {
      if (this.localWorksheet.productId) {
        return await this.fetchCategory(this.localWorksheet.productId).then(
          (resp) => {
            this.productInfo = resp;

            return this.productInfo;
          }
        );
      } else {
        return null;
      }
    },
    async deleteWorksheet() {
      let confirm = await this.$refs.confirm.open(
        this.$vuetify.lang.t("$vuetify.base.confirmDelete"),
        this.$vuetify.lang.t("$vuetify.worksheet.deleteConfirmMsg")
      );
      if (confirm) {
        this.deleteProcessWorksheet(this.localWorksheet.id)
          .then(() => {
            this.addSuccessMessage(
              this.$vuetify.lang.t("$vuetify.worksheet.deleteSuccessMsg")
            );
            this.$emit("deleted", this.localWorksheet);
          })
          .catch(() => {
            this.addErrorMessage("Server responded with an error.");
          });
      }
    },
    editEntry(entry) {
      if (entry && entry.id) {
        this.currentEntry = entry;
        this.entriesDialog = false;
      }
    },
    async toggleStatus() {
      const originalStatus = this.localWorksheet.status;

      let toggleStatus = "PENDING";

      switch (originalStatus) {
        case "SUBMITTED":
          toggleStatus = "PENDING";
          break;
        case "PENDING":
        case "USED":
          toggleStatus = "SUBMITTED";
          break;
      }

      let confirm = await this.$refs.confirm.open(
        "Confirm Change Status",
        "Please confirm you would like to change status to " + toggleStatus
      );

      if (confirm) {
        if (originalStatus === "USED") {
          this.$axios
            .put(`/processWorksheet/unmark-used/${this.localWorksheet.id}`)
            .then(() => {
              this.localWorksheet.status = toggleStatus;
              this.addSuccessMessage("Worksheet status updated successfully");
              this.$emit("update:worksheet", this.localWorksheet);
            })
            .catch((error) => {
              console.log(error);
              this.localWorksheet.status = originalStatus;
              const message = error.data.message || "Error updating worksheet status";
              this.addErrorMessage(message);
            });
        } else if (originalStatus === "SUBMITTED" && toggleStatus === "PENDING") {
          this.$axios
            .put(`/processWorksheet/unsubmit/${this.localWorksheet.id}`)
            .then(() => {
              this.localWorksheet.status = "PENDING";
              this.localWorksheet.processTime = null;
              this.localWorksheet.submittedTime = null;
              this.stage = 1;
              this.addSuccessMessage("Worksheet status updated successfully");
              this.$emit("update:worksheet", this.localWorksheet);
            })
            .catch((error) => {
              console.log(error);
              this.localWorksheet.status = originalStatus;
              const message = error.data.message || "Error updating worksheet status";
              this.addErrorMessage(message);
            });
        } else {
          this.localWorksheet.status = toggleStatus;
          this.updateProcessWorksheet(this.localWorksheet)
            .then(() => {
              this.addSuccessMessage("Worksheet status updated successfully");
              this.$emit("update:worksheet", this.localWorksheet);
            })
            .catch((error) => {
              console.log(error);
              this.localWorksheet.status = originalStatus;
              const message = error.data.message || "Error updating worksheet status";
              this.addErrorMessage(message);
            });
        }
      }
    },
    async reassignAll() {
      let confirm = await this.$refs.confirm.open(
        this.$vuetify.lang.t("$vuetify.worksheet.reassignAll"),
        this.$vuetify.lang.t("$vuetify.worksheet.reassignAllConfirm")
      );
      if (confirm) {
        reassignAllInventory(this.localWorksheet.id)
          .then((resp) => {
            const data = resp.data;
            if (data && data.shortfalls && data.shortfalls > 0) {
              this.addSuccessMessage(
                this.$vuetify.lang.t("$vuetify.worksheet.reassignPartial")
              );
            } else {
              this.addSuccessMessage(
                this.$vuetify.lang.t("$vuetify.worksheet.reassignSuccess")
              );
            }
            this.refreshWorksheet();
          })
          .catch(() => {
            this.addErrorMessage(
              this.$vuetify.lang.t("$vuetify.worksheet.reassignFailed")
            );
          });
      }
    },
    jobChanged() {
      this.$emit("jobUpdated");
    },
    handleWorksheetUpdate(updatedWorksheet) {
      // Reload product info if product id has changed
      if (this.localWorksheet.productId !== updatedWorksheet.productId) {
        this.productInfo = null;
        this.loadProductInfo();
      }
      this.localWorksheet = updatedWorksheet;

      this.$emit("update:worksheet", this.localWorksheet);
      this.$emit("updated", updatedWorksheet);
    },
    handleAddEntry(newEntries) {
      // Handle both single entry (legacy) and array of entries (new behavior)
      const entriesToAdd = Array.isArray(newEntries) ? newEntries : [newEntries];
      
      if (Array.isArray(this.localWorksheet.entries)) {
        // Create a new array to ensure Vue reactivity
        const updatedEntries = [...this.localWorksheet.entries, ...entriesToAdd];
        this.$set(this.localWorksheet, 'entries', updatedEntries);
      } else {
        // Initialize entries as an array if not already
        this.$set(this.localWorksheet, 'entries', [...entriesToAdd]);
      }

      this.sumWeight();

      // Emit the updated worksheet
      this.$emit("update:worksheet", this.localWorksheet);

      // Print here, not where the entry was saved: the lot number is the
      // entry's POSITION in this list, so it does not exist until the entry has
      // been added — and on LME products that number is encoded into the QR.
      this.autoPrintEntries(entriesToAdd);
    },

    // Reprint one entry's sticker, straight to the printer beside this station.
    // Used when a label is torn or lost, so it deliberately does not care
    // whether the worksheet is still open for entry.
    async reprintEntry(entry) {
      const printer = this.$refs.autoLabelPrint;
      if (!printer) {
        return;
      }
      try {
        await printer.printEntry(entry, this.localWorksheet.entries);
      } catch (error) {
        // printEntry reports its own failures on screen.
      }
    },

    // One sticker per saved entry, if this phone is set up to print. Never
    // blocks or fails the save: the entry is the record, the label is a
    // convenience, and the batch print page remains the fallback.
    async autoPrintEntries(entriesToAdd) {
      const printer = this.$refs.autoLabelPrint;
      if (!printer || !printer.autoPrint) {
        return;
      }
      await this.$nextTick();
      // Split entries (FIFO/LIFO) collapse into one sticker, so print one label
      // per group rather than one per row.
      const seen = new Set();
      for (const entry of entriesToAdd) {
        const groupId = entry.entryGroupId || entry.id;
        if (seen.has(groupId)) {
          continue;
        }
        seen.add(groupId);
        try {
          await printer.printEntry(entry, this.localWorksheet.entries);
        } catch (error) {
          // Printing must never take the save down with it.
        }
      }
    },

    handleUpdateEntry(updatedEntry) {
      const index = this.localWorksheet.entries.findIndex(
        (e) => e.id === updatedEntry.id
      );
      if (index !== -1) {
        this.$set(this.localWorksheet.entries, index, updatedEntry);
      }
      this.sumWeight();

      // Emit the updated worksheet
      this.$emit("update:worksheet", this.localWorksheet);
    },

    handleUpdateEntries(updatedEntries) {
      // Handle multiple entry updates (for grouped entries)
      if (Array.isArray(updatedEntries) && updatedEntries.length > 0) {
        updatedEntries.forEach(updatedEntry => {
          const index = this.localWorksheet.entries.findIndex(
            (e) => e.id === updatedEntry.id
          );
          if (index !== -1) {
            this.$set(this.localWorksheet.entries, index, updatedEntry);
          }
        });
        
        this.sumWeight();

        // Emit the updated worksheet
        this.$emit("update:worksheet", this.localWorksheet);
      }
    },

    handleUpdateCurrentEntry(newCurrentEntry) {
      this.currentEntry = newCurrentEntry;
    },
    handleDeleteEntry(deletedEntry) {
      // The lot number on a label is the entry's position, so removing one
      // renumbers every entry after it — and any sticker already printed for
      // those is now on the goods with the wrong number. Nothing can recall a
      // printed sticker, so at least say so.
      const printer = this.$refs.autoLabelPrint;
      const removedAt = printer
        ? printer.lotIndexFor(this.localWorksheet.entries, deletedEntry)
        : 0;
      const entries = this.localWorksheet.entries || [];
      const wasLast =
        removedAt > 0 &&
        printer &&
        removedAt ===
          printer.lotIndexFor(entries, entries[entries.length - 1]);
      if (removedAt && !wasLast) {
        this.addWarningMessage(
          `ลบรายการที่ ${removedAt} แล้ว — ป้ายที่พิมพ์ไว้ตั้งแต่หมายเลข ${removedAt} เป็นต้นไปมีเลขล็อตผิด ต้องพิมพ์ใหม่ / ` +
            `Entry ${removedAt} deleted — labels already printed from ${removedAt} onward now show the wrong lot number and must be reprinted.`
        );
      }
      if (deletedEntry && deletedEntry.id) {
        // If it's a grouped entry, remove all entries with the same entryGroupId
        if (deletedEntry._isGrouped && deletedEntry.entryGroupId) {
          // Remove all entries in the group
          this.localWorksheet.entries = this.localWorksheet.entries.filter(
            (e) => e.entryGroupId !== deletedEntry.entryGroupId
          );
          const removedCount = deletedEntry._groupEntries ? deletedEntry._groupEntries.length : 1;
          this.localWorksheet.entryCount =
            typeof this.localWorksheet.entryCount === "number"
              ? this.localWorksheet.entryCount - removedCount
              : 0;
        } else {
          // Single entry deletion
          const index = this.localWorksheet.entries.findIndex(
            (e) => e.id === deletedEntry.id
          );
          if (index !== -1) {
            this.localWorksheet.entries.splice(index, 1);
            this.localWorksheet.entryCount =
              typeof this.localWorksheet.entryCount === "number"
                ? this.localWorksheet.entryCount - 1
                : 0;
          }
        }
        
        this.sumWeight();

        this.addSuccessMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.entryDeleted")
        );

        // Emit the updated worksheet
        this.$emit("update:worksheet", this.localWorksheet);
      }
    },
    async handleResolvedEntry() {
      try {
        await this.refreshWorksheet({ silent: false });
      } catch (error) {
        const message =
          (error && error.data && error.data.message) ||
          "Error refreshing worksheet data.";
        this.addErrorMessage(message);
      }
    },
  },
};
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
